import React, { useMemo } from 'react';
import {
  alpha,
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  useTheme,
} from '@mui/material';
import { Eye, MessageCircleIcon, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useGetContractQuery } from '@/apis/extended/contractApi';
import {
  GetCounterProposalResponse,
  useGetCounterProposalQuery,
  useGetNegotiationHistoryQuery,
} from '@/apis/extended/negotiation';
import { Button } from '@/components/ui/button';
import { parseDateOnly } from '@/utils/dateOnly';
import { productAddressLine } from '@/utils/productAddress';
import { getProductContractId } from '@/utils/productContractHelpers';
import { Product } from '@/types/ProductType';
import { Badge } from '../ui/badge';

type ProductContractSummaryProps = {
  product?: Product;
  contractId?: string;
  userRole?: 'buyer' | 'seller' | 'agent';
};

export const ProductContractSummary = ({
  product,
  contractId,
  userRole = 'buyer',
}: ProductContractSummaryProps) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const contractIdToUse =
    contractId || (product ? getProductContractId(product) : '') || '';

  const { data: contract, refetch: refetchContract } = useGetContractQuery(
    contractIdToUse,
    {
      skip: !contractIdToUse,
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
      pollingInterval: 0,
    }
  );

  const { data: counterProposalRaw, refetch: refetchCounterProposal } =
    useGetCounterProposalQuery(contractIdToUse, {
      skip: !contractIdToUse,
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
      pollingInterval: 0,
    });
  const counterProposal = counterProposalRaw as
    | GetCounterProposalResponse
    | undefined;

  // Get property address from contract/product (source of truth) or legacy agentSellerInfo
  const propertyAddress =
    contract?.product?.address?.fullAddress ||
    contract?.product?.address?.street ||
    contract?.agentSellerInfo?.propertyAddress ||
    productAddressLine(contract?.product) ||
    productAddressLine(product) ||
    'N/A';

  // Get offer price
  const offerPrice =
    contract?.offerSpecifics?.offerPrice ||
    contract?.agentOfferInfo?.offerPrice ||
    product?.price ||
    0;

  // Get closing date
  const closingDate =
    contract?.timeline?.closingDate ||
    contract?.offerSpecifics?.closingDate ||
    null;

  // Get financing commitment date
  const financingCommitmentDate =
    contract?.offerSpecifics?.financingCommittmentDate || null;

  // Get financing type
  const financingType =
    (contract as any)?.contingencies?.financingContingency ||
    contract?.contingency?.financingContingency ||
    contract?.offerSpecifics?.loanProgram ||
    'N/A';

  // Get contract created date as offer date
  const offerDate =
    (contract as any)?.createdAt ||
    contract?.timeline?.offerGoodUntillDate ||
    null;

  const checkItems = useMemo(() => {
    const items = [
      {
        label: 'Agent received the contract',
        value: Boolean(contract?.agentReceived),
      },
      {
        label: 'Agent confirmed contract details',
        value: Boolean(contract?.agentSign?.isStored),
      },
      {
        label: 'Contract email sent to seller',
        value: Boolean(contract?.sellerEmailSent),
      },
      {
        label: 'Seller received the contract',
        value: Boolean(contract?.sellerReceived),
      },
      {
        label: 'Seller submitted the contract',
        value: Boolean(contract?.sellerSign?.isStored),
      },
    ];
    return items;
  }, [contract]);

  const showNegotiationButton = Boolean(
    counterProposal &&
      (counterProposal as GetCounterProposalResponse).negotiation?._id
  );
  const { data: counterproposalstatus, refetch: refetchNegotiationHistory } =
    useGetNegotiationHistoryQuery(contractIdToUse, {
      skip: !contractIdToUse || !showNegotiationButton,
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
      pollingInterval: 0,
    });

  const handleRefreshData = () => {
    refetchContract();
    refetchCounterProposal();
    if (showNegotiationButton) {
      refetchNegotiationHistory();
    }
  };

  return (
    <Card
      sx={{
        border: `1px solid ${theme.palette.greyShades.border1}`,
        borderRadius: 3,
        boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.06)}`,
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
      }}
    >
      <CardHeader
        title="Contract Status"
        avatar={<i className="fa-light fa-clipboard-list-check" />}
        sx={{
          borderBottom: `1px solid ${theme.palette.greyShades.border1}`,
          pb: 2,
          '& .MuiCardHeader-title': {
            color: theme.palette.text.primary,
            fontWeight: 700,
            fontSize: '1.1rem',
          },
          '& .MuiCardHeader-avatar': {
            color: theme.palette.primary.main,
          },
        }}
        action={
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRefreshData}
            className="h-8 w-8 p-0"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        }
      />
      <span className="flex items-center gap-2 justify-end mr-2 mt-2">
        {product?.contract && showNegotiationButton && (
          <Badge
            variant="outline"
            className="bg-gray-100 text-gray-500 capitalize"
          >
            <p>
              {' '}
              {typeof counterproposalstatus?.negotiations[0]
                ?.NegotiationStatus === 'object'
                ? counterproposalstatus?.negotiations[0]?.NegotiationStatus
                    ?.value
                : counterproposalstatus?.negotiations[0]?.NegotiationStatus}
            </p>
          </Badge>
        )}
      </span>
      <CardContent>
        <Box>
          {/* Property Address */}
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              sx={{ color: theme.palette.greyShades.grey1, display: 'block' }}
            >
              Property Address
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.primary, fontWeight: 600 }}
            >
              {propertyAddress}
            </Typography>
          </Box>

          {/* Contract Status Badge */}
          {contract && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                sx={{
                  display: 'inline-block',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  backgroundColor:
                    (contract as any).contractStatus === 'ratified'
                      ? theme.palette.success.main
                      : (contract as any).contractStatus === 'negotiating' ||
                        (contract as any).contractStatus === 'draft'
                      ? theme.palette.warning.main
                      : (contract as any).contractStatus ===
                        'awaiting_signatures'
                      ? theme.palette.info.main
                      : theme.palette.greyShades.grey2,
                  color: theme.palette.common.white,
                  textTransform: 'capitalize',
                }}
              >
                {contract.contractStatus?.replace(/_/g, ' ') || 'Draft'}
              </Typography>
            </Box>
          )}

          {/* Contract Details Grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 2,
              mb: 3,
            }}
          >
            <Box>
              <Typography
                variant="caption"
                sx={{ color: theme.palette.greyShades.grey1, display: 'block' }}
              >
                Offer Amount
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.success.main, fontWeight: 600 }}
              >
                ${offerPrice?.toLocaleString() || '0'}
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="caption"
                sx={{ color: theme.palette.greyShades.grey1, display: 'block' }}
              >
                Offer Date
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.primary, fontWeight: 600 }}
              >
                {offerDate ? new Date(offerDate).toLocaleDateString() : 'N/A'}
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="caption"
                sx={{ color: theme.palette.greyShades.grey1, display: 'block' }}
              >
                Closing Date
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.primary, fontWeight: 600 }}
              >
                {closingDate
                  ? parseDateOnly(closingDate)?.toLocaleDateString()
                  : 'N/A'}
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="caption"
                sx={{ color: theme.palette.greyShades.grey1, display: 'block' }}
              >
                Financing Commitment
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.primary, fontWeight: 600 }}
              >
                {financingCommitmentDate
                  ? new Date(financingCommitmentDate).toLocaleDateString()
                  : 'N/A'}
              </Typography>
            </Box>

            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography
                variant="caption"
                sx={{ color: theme.palette.greyShades.grey1, display: 'block' }}
              >
                Financing Type
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 600,
                  textTransform: 'capitalize',
                }}
              >
                {financingType === 'at_least_purchase_price'
                  ? 'Mortgage Financing'
                  : financingType === 'cash'
                  ? 'Cash Offer'
                  : financingType === 'N/A'
                  ? 'N/A'
                  : financingType?.replace(/_/g, ' ')}
              </Typography>
            </Box>
          </Box>

          {/* Divider */}
          {contract && (
            <>
              <Box
                sx={{
                  borderTop: `1px solid ${theme.palette.greyShades.border1}`,
                  my: 2,
                }}
              />

              {/* Contract Progress */}
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.greyShades.grey1,
                  display: 'block',
                  mb: 1,
                }}
              >
                Contract Progress
              </Typography>
            </>
          )}

          {checkItems.map((item, idx) => (
            <Box
              key={idx}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                ml: 1,
                mt: 2,
              }}
            >
              {item.value ? (
                <i className="fa-light fa-check color-success" />
              ) : (
                <i className="fa-light fa-close color-error" />
              )}
              <Typography
                sx={{ color: theme.palette.greyShades.grey1 }}
                variant="body2"
              >
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
        {product?.contract && (
          <div className="flex gap-2 flex-row mt-4">
            {contract?.contractStatus === 'ratified' ? (
              <>
                {/* Ratified Contract Actions - Role-specific dashboards */}
                <Button
                  className="bg-green-600 text-white border border-green-600 hover:bg-green-700 hover:text-white cursor-pointer"
                  variant="outline"
                  onClick={() => {
                    if (userRole === 'buyer') {
                      navigate(`/buyer/property-dashboard/${contractIdToUse}`);
                    } else if (userRole === 'seller') {
                      navigate(`/seller/property-dashboard/${contractIdToUse}`);
                    } else if (userRole === 'agent') {
                      navigate(`/agent/property-dashboard/${contractIdToUse}`);
                    }
                  }}
                >
                  <i className="fa-light fa-chart-line w-2 h-2" />
                  {userRole === 'buyer'
                    ? 'Purchase Dashboard'
                    : userRole === 'seller'
                    ? 'Sale Dashboard'
                    : 'Property Dashboard'}
                </Button>
                <Button
                  className="bg-white text-black border border-gray-300 hover:bg-gray-100 hover:text-black cursor-pointer"
                  variant="outline"
                  onClick={() => {
                    navigate(`/view-contract/${contractIdToUse}`);
                  }}
                >
                  <Eye className="w-2 h-2" /> Review Contract
                </Button>
              </>
            ) : showNegotiationButton ? (
              <>
                {/* Active Contract Actions - Role-specific negotiation */}
                <Button
                  className="bg-white text-black border border-gray-300 hover:bg-gray-100 hover:text-black cursor-pointer"
                  variant="outline"
                  onClick={() => {
                    navigate(
                      `/contract/counter-proposal-history/${contractIdToUse}`
                    );
                  }}
                >
                  <Eye className="w-2 h-2" /> View Counter Proposals
                </Button>
                <Button
                  className=" bg-white text-black border border-gray-300 hover:bg-gray-100 hover:text-black cursor-pointer"
                  variant="outline"
                  onClick={() => {
                    if (userRole === 'buyer') {
                      navigate(`/buyer/negotiate/${contractIdToUse}`);
                    } else if (userRole === 'seller') {
                      navigate(`/seller/negotiate/${contractIdToUse}`);
                    } else if (userRole === 'agent') {
                      navigate(`/agent/negotiate/${contractIdToUse}`);
                    }
                  }}
                >
                  <MessageCircleIcon className="w-2 h-2" /> Negotiate Proposal
                </Button>
              </>
            ) : null}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
